/** Ray-cast point-in-polygon test (normalized or any consistent space). */
export const pointInPolygon = (point, polygon) => {
    if (polygon.length < 3) {
        return true;
    }
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const a = polygon[i];
        const b = polygon[j];
        const intersects = a.y > point.y !== b.y > point.y &&
            point.x <
                ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y || Number.EPSILON) + a.x;
        if (intersects) {
            inside = !inside;
        }
    }
    return inside;
};
const dist2 = (a, b) => {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return dx * dx + dy * dy;
};
/** Closest point on segment AB to P. */
const closestOnSegment = (p, a, b) => {
    const abx = b.x - a.x;
    const aby = b.y - a.y;
    const len2 = abx * abx + aby * aby;
    if (len2 < 1e-12) {
        return { x: a.x, y: a.y };
    }
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2));
    return { x: a.x + abx * t, y: a.y + aby * t };
};
/**
 * Project an outside point onto the nearest polygon edge, then inset slightly
 * toward the polygon centroid so the center lands inside.
 */
export const projectPointInsidePolygon = (point, polygon) => {
    if (polygon.length < 3 || pointInPolygon(point, polygon)) {
        return point;
    }
    let best = polygon[0];
    let bestD = Infinity;
    for (let i = 0; i < polygon.length; i++) {
        const a = polygon[i];
        const b = polygon[(i + 1) % polygon.length];
        const candidate = closestOnSegment(point, a, b);
        const d = dist2(point, candidate);
        if (d < bestD) {
            bestD = d;
            best = candidate;
        }
    }
    const centroid = polygon.reduce((acc, p) => ({ x: acc.x + p.x / polygon.length, y: acc.y + p.y / polygon.length }), { x: 0, y: 0 });
    const inset = 0.02;
    return {
        x: best.x + (centroid.x - best.x) * inset,
        y: best.y + (centroid.y - best.y) * inset,
    };
};
/** Axis-aligned rect center in the same unit space as the outline (0–1). */
export const rectCenterNormalized = (x, y, width, height, canvasWidth, canvasHeight) => ({
    x: canvasWidth > 0 ? (x + width / 2) / canvasWidth : 0,
    y: canvasHeight > 0 ? (y + height / 2) / canvasHeight : 0,
});
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
/**
 * Keep a table/object placement so its center stays inside the hall footprint.
 * Canvas AABB is still respected.
 */
export const clampPlacementInsideOutline = (x, y, width, height, outline, canvasWidth, canvasHeight) => {
    const maxX = Math.max(0, canvasWidth - width);
    const maxY = Math.max(0, canvasHeight - height);
    let nextX = clamp(x, 0, maxX);
    let nextY = clamp(y, 0, maxY);
    const center = rectCenterNormalized(nextX, nextY, width, height, canvasWidth, canvasHeight);
    if (pointInPolygon(center, outline)) {
        return { x: nextX, y: nextY };
    }
    const inside = projectPointInsidePolygon(center, outline);
    nextX = clamp(inside.x * canvasWidth - width / 2, 0, maxX);
    nextY = clamp(inside.y * canvasHeight - height / 2, 0, maxY);
    return { x: nextX, y: nextY };
};
/** Outline bounding box in canvas pixels. */
export const outlineBoundsPixels = (outline, canvasWidth, canvasHeight) => {
    const pts = outline.length >= 3 ? outline : [{ x: 0, y: 0 }, { x: 1, y: 1 }];
    let minX = 1;
    let minY = 1;
    let maxX = 0;
    let maxY = 0;
    pts.forEach(p => {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
    });
    return {
        x: minX * canvasWidth,
        y: minY * canvasHeight,
        width: Math.max(1, (maxX - minX) * canvasWidth),
        height: Math.max(1, (maxY - minY) * canvasHeight),
    };
};
/** Space for chair overhang outside the table silhouette. */
const SEAT_OVERHANG = 28;
/** Clear air gap between neighboring seat rings. */
const TABLE_GAP = 20;
/** Arrange may grow the hall past the normal editor max. */
const ARRANGE_MAX_CANVAS_WIDTH = 4800;
const ARRANGE_MAX_CANVAS_HEIGHT = 3600;
/** Resolve the exact rows×cols the arrange UI and engine both use. */
export const resolveArrangeGridDims = (tableCount, options, areaW, maxTableWidth) => {
    const n = Math.max(0, Math.round(tableCount));
    if (n <= 0) {
        return { rows: 0, cols: 0 };
    }
    if (options?.cols != null && options?.rows != null) {
        let cols = Math.max(1, Math.round(options.cols));
        let rows = Math.max(1, Math.round(options.rows));
        // Never drop tables: expand columns so every table gets a slot.
        if (cols * rows < n) {
            cols = Math.ceil(n / rows);
        }
        return { cols, rows };
    }
    if (options?.cols != null) {
        const cols = Math.min(n, Math.max(1, Math.round(options.cols)));
        return { cols, rows: Math.ceil(n / cols) };
    }
    if (options?.rows != null) {
        const rows = Math.min(n, Math.max(1, Math.round(options.rows)));
        return { rows, cols: Math.ceil(n / rows) };
    }
    // Auto: as many full-size columns as the hall allows with even edge gaps.
    // space-evenly needs (cols + 1) gaps: cols * cellW + (cols + 1) * gap <= areaW
    const cellW = (maxTableWidth || 100) + SEAT_OVERHANG * 2;
    const usable = Math.max(80, areaW || 800);
    const cols = Math.max(1, Math.min(n, Math.floor((usable - TABLE_GAP) / (cellW + TABLE_GAP)) || 1));
    return { cols, rows: Math.ceil(n / cols) };
};
/**
 * Minimum grid footprint when gaps are equal at edges and between cells
 * (CSS justify/align space-evenly).
 */
const gridFootprint = (cols, rows, cellInnerW, cellInnerH, seat, gap) => {
    const cellW = cellInnerW + seat * 2;
    const cellH = cellInnerH + seat * 2;
    return {
        width: cols * cellW + (cols + 1) * gap,
        height: rows * cellH + (rows + 1) * gap,
        cellW,
        cellH,
    };
};
/** Normalized outline span (0–1); used when growing canvas for usable area. */
const outlineNormSpan = (outline) => {
    const pts = outline.length >= 3 ? outline : [{ x: 0, y: 0 }, { x: 1, y: 1 }];
    let minX = 1;
    let minY = 1;
    let maxX = 0;
    let maxY = 0;
    pts.forEach(p => {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
    });
    return {
        width: Math.max(0.05, maxX - minX),
        height: Math.max(0.05, maxY - minY),
    };
};
/**
 * @deprecated Prefer growing the hall. Kept for callers that still import it.
 * Returns seat/gap compression only (scale always 1 for table size).
 */
export const computeArrangeFitScale = (cols, rows, maxW, maxH, areaW, areaH) => {
    if (cols <= 0 || rows <= 0 || maxW <= 0 || maxH <= 0) {
        return { scale: 1, seat: SEAT_OVERHANG, gap: TABLE_GAP };
    }
    let seat = SEAT_OVERHANG;
    let gap = TABLE_GAP;
    const full = gridFootprint(cols, rows, maxW, maxH, seat, gap);
    if (full.width <= areaW && full.height <= areaH) {
        return { scale: 1, seat, gap };
    }
    // Last resort at canvas cap: compress seat/gap only, never table size.
    const need = Math.min(areaW / full.width, areaH / full.height);
    const padScale = Math.max(0.2, Math.min(1, need));
    seat = Math.max(4, SEAT_OVERHANG * padScale);
    gap = Math.max(4, TABLE_GAP * padScale);
    return { scale: 1, seat, gap };
};
/**
 * Pack tables into the hall on an exact rows×cols grid at full size.
 * Gaps are space-evenly: equal space at hall edges and between tables.
 * When the minimum grid does not fit, grows the canvas (aspect may change).
 * Preview and apply must both call this (same math).
 */
export const arrangeTablesInHall = (tables, outline, canvasWidth, canvasHeight, padding = 36, options) => {
    if (tables.length === 0) {
        return { rows: 0, cols: 0, scale: 1, canvas: null, placements: {} };
    }
    const maxW = Math.max(...tables.map(t => t.width), 80);
    const maxH = Math.max(...tables.map(t => t.height), 80);
    const currentBounds = outlineBoundsPixels(outline, canvasWidth, canvasHeight);
    const currentAreaW = Math.max(80, currentBounds.width - padding * 2);
    const currentAreaH = Math.max(80, currentBounds.height - padding * 2);
    const { rows, cols } = resolveArrangeGridDims(tables.length, options, currentAreaW, maxW);
    let seat = SEAT_OVERHANG;
    let minGap = TABLE_GAP;
    let footprint = gridFootprint(cols, rows, maxW, maxH, seat, minGap);
    const span = outlineNormSpan(outline);
    // Canvas size needed so outline bounds can hold the full-size grid + padding.
    let nextCanvasW = Math.max(canvasWidth, Math.ceil((footprint.width + padding * 2) / span.width));
    let nextCanvasH = Math.max(canvasHeight, Math.ceil((footprint.height + padding * 2) / span.height));
    nextCanvasW = Math.min(ARRANGE_MAX_CANVAS_WIDTH, nextCanvasW);
    nextCanvasH = Math.min(ARRANGE_MAX_CANVAS_HEIGHT, nextCanvasH);
    const hallGrew = nextCanvasW > canvasWidth + 0.5 || nextCanvasH > canvasHeight + 0.5;
    // If still over after the arrange canvas cap, compress seat/gap only.
    const cappedBounds = outlineBoundsPixels(outline, nextCanvasW, nextCanvasH);
    let areaW = Math.max(80, cappedBounds.width - padding * 2);
    let areaH = Math.max(80, cappedBounds.height - padding * 2);
    if (footprint.width > areaW + 0.5 || footprint.height > areaH + 0.5) {
        const compressed = computeArrangeFitScale(cols, rows, maxW, maxH, areaW, areaH);
        seat = compressed.seat;
        minGap = compressed.gap;
        footprint = gridFootprint(cols, rows, maxW, maxH, seat, minGap);
    }
    const bounds = outlineBoundsPixels(outline, nextCanvasW, nextCanvasH);
    const areaX = bounds.x + padding;
    const areaY = bounds.y + padding;
    areaW = Math.max(80, bounds.width - padding * 2);
    areaH = Math.max(80, bounds.height - padding * 2);
    const cellW = maxW + seat * 2;
    const cellH = maxH + seat * 2;
    // space-evenly: (n + 1) equal gaps including hall edges.
    // When the hall is larger than the minimum footprint, gaps grow so tables
    // stretch across the whole usable area instead of clustering in the center.
    const gapX = cols > 0 ? Math.max(0, (areaW - cols * cellW) / (cols + 1)) : 0;
    const gapY = rows > 0 ? Math.max(0, (areaH - rows * cellH) / (rows + 1)) : 0;
    const placements = {};
    tables.forEach((table, index) => {
        const col = index % cols;
        const row = Math.floor(index / cols);
        if (row >= rows) {
            return;
        }
        const width = table.width;
        const height = table.height;
        const cellX = areaX + gapX + col * (cellW + gapX);
        const cellY = areaY + gapY + row * (cellH + gapY);
        const x = cellX + seat + (maxW - width) / 2;
        const y = cellY + seat + (maxH - height) / 2;
        placements[table.uuid] = {
            x: Math.max(0, Math.min(x, Math.max(0, nextCanvasW - width))),
            y: Math.max(0, Math.min(y, Math.max(0, nextCanvasH - height))),
            width,
            height,
            rotation: 0,
        };
    });
    return {
        rows,
        cols,
        scale: 1,
        canvas: hallGrew ? { width: nextCanvasW, height: nextCanvasH } : null,
        placements,
    };
};
/** Helpful row×col presets for a table count (e.g. 10 → 2×5, 5×2). */
export const suggestTableGridLayouts = (tableCount) => {
    const n = Math.max(0, Math.round(tableCount));
    if (n <= 0) {
        return [];
    }
    const out = [
        { label: 'Auto', tip: 'Fit the hall', rows: undefined, cols: undefined },
        { label: '1 row', tip: `${n} across`, rows: 1 },
    ];
    if (n >= 2) {
        out.push({
            label: '2 rows',
            tip: `${Math.ceil(n / 2)} per row`,
            rows: 2,
        });
    }
    if (n >= 3) {
        out.push({
            label: '3 rows',
            tip: `${Math.ceil(n / 3)} per row`,
            rows: 3,
        });
    }
    if (n >= 4) {
        out.push({
            label: '4 rows',
            tip: `${Math.ceil(n / 4)} per row`,
            rows: 4,
        });
    }
    out.push({ label: '1 col', tip: `${n} down`, cols: 1 });
    if (n >= 2) {
        out.push({
            label: '2 cols',
            tip: `${Math.ceil(n / 2)} per col`,
            cols: 2,
        });
    }
    if (n >= 3) {
        out.push({
            label: '3 cols',
            tip: `${Math.ceil(n / 3)} per col`,
            cols: 3,
        });
    }
    if (n >= 4) {
        out.push({
            label: '4 cols',
            tip: `${Math.ceil(n / 4)} per col`,
            cols: 4,
        });
    }
    // Exact factor grids (e.g. 10 → 2×5 and 5×2) when not already covered.
    const seen = new Set(out.map(item => `${item.rows ?? 'a'}x${item.cols ?? 'a'}`));
    for (let rows = 2; rows <= Math.min(6, n); rows++) {
        if (n % rows !== 0) {
            continue;
        }
        const cols = n / rows;
        const key = `${rows}x${cols}`;
        if (seen.has(key) || seen.has(`${rows}x${undefined}`)) {
            continue;
        }
        // Skip if same as a simple "N rows" chip with matching cols.
        if (rows <= 4 && cols === Math.ceil(n / rows)) {
            continue;
        }
        out.push({
            label: `${rows}×${cols}`,
            tip: 'Even grid',
            rows,
            cols,
        });
        seen.add(key);
    }
    return out;
};
/** First open-ish spot inside the hall for a newly added table/object. */
export const findSpotInsideOutline = (width, height, outline, canvasWidth, canvasHeight, occupied = [], padding = 40) => {
    const bounds = outlineBoundsPixels(outline, canvasWidth, canvasHeight);
    const step = Math.max(24, Math.min(width, height) * 0.5);
    const overlaps = (x, y) => occupied.some(item => x < item.x + item.width &&
        x + width > item.x &&
        y < item.y + item.height &&
        y + height > item.y);
    for (let y = bounds.y + padding; y <= bounds.y + bounds.height - height - padding; y += step) {
        for (let x = bounds.x + padding; x <= bounds.x + bounds.width - width - padding; x += step) {
            const center = rectCenterNormalized(x, y, width, height, canvasWidth, canvasHeight);
            if (pointInPolygon(center, outline) && !overlaps(x, y)) {
                return { x, y };
            }
        }
    }
    return clampPlacementInsideOutline(padding, padding, width, height, outline, canvasWidth, canvasHeight);
};
